// Which folds are closed, remembered across reloads.
//
// Only ids the user has actually TOUCHED are stored. An id present in the map carries
// their explicit choice and always wins; an absent id falls back to the caller's
// default (on the board: open work → expanded, nothing open → collapsed). Keeping the
// untouched ones out is what lets a client that gains its first task open itself,
// instead of a stale `true` written when it was empty holding it shut forever.
//
// The STORAGE KEY is a parameter because there is now a second folding screen — the
// client card's work tree, whose retainers and jobs nest two deep. Sharing one map
// between them would let a board client and a retainer with the same numeric id fold
// each other, so each screen gets its own key and each namespaces its ids within it
// (the work tree uses `r:3` / `j:3`, since a retainer and a job can both be 3).
import { useCallback, useState } from 'react';

const BOARD_KEY = 'pressbooks.board.collapsed';

// localStorage throws in private mode and can hold a value someone else wrote.
// Neither is worth an error on screen — a lost fold just means the defaults apply.
const read = (key) => {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key));
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const write = (key, map) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(map));
  } catch {
    /* nothing to do — the fold simply will not survive this reload */
  }
};

export default function useCollapsedGroups(key = BOARD_KEY) {
  const [map, setMap] = useState(() => read(key));

  const store = useCallback((ids, collapsed) => {
    setMap((m) => {
      const next = { ...m, ...Object.fromEntries(ids.map((id) => [id, collapsed])) };
      write(key, next);
      return next;
    });
  }, [key]);

  const setCollapsed = useCallback((id, collapsed) => store([id], collapsed), [store]);
  const setAll = useCallback((ids, collapsed) => store(ids, collapsed), [store]);
  const isCollapsed = useCallback((id, fallback) => (id in map ? map[id] : fallback), [map]);

  return { isCollapsed, setCollapsed, setAll };
}
